import { useState } from "react";
import {
  useRecordContext,
} from "react-admin";

//Common styles
import { BlueBorderContainer, BlueTOpBorderContainer, ButtonContainer, Paragraph, SectionTitle, ShowLabel, ThreeColNRowGrid, WorkspacePanel, WorkSpaceTitleRow } from "../../styles/commonStyles";

//Common components
import TransactionButton from "../Common/TransactionButton";
import ImagePreviewerDialog from "../Common/ImagePreviewerDialog";

interface CuisineMetaShowProps {
  onAddImage: (record: any) => void;
  onUpdate: (record: any) => void;
  onDelete: (record: any) => void;

}

const FALLBACK_IMAGE =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRODZob7ROpqlx8WXwjueNwTOlC6Qum-yMiyfXiFRXkdhkHgybCpdvM1UpaXCL1ycfO8NI&usqp=CAU";

const getEquipmentName = (equipment: any) => {
  return (
    equipment?.EquipmentModelV2?.name ||
    equipment?.equipment?.name ||
    equipment?.name ||
    "-"
  );
};

const getInstructionCategoryName = (instruction: any) => {
  return (
    instruction?.InstructionCategoryModel?.name ||
    instruction?.DishInstructionCategory?.name ||
    instruction?.category?.name ||
    instruction?.categoryName ||
    (instruction?.categoryId ? `Category #${instruction.categoryId}` : "Instruction")
  );
};

const CuisineMetaShow = ({
  onAddImage,
  onUpdate,
  onDelete,
}: CuisineMetaShowProps) => {

  const [previewOpen, setPreviewOpen] = useState(false);

  let record = useRecordContext();

  if (!record) return null;

  const imageSrc = record?.image || FALLBACK_IMAGE;
  const equipments = record?.equipments || [];
  const instructions = record?.instructions || [];

  return (
    <>
      <div style={{ display: "flex", gap: '20px', alignItems: "flex-start" }}>
        <WorkspacePanel>

          <BlueBorderContainer>
            <WorkSpaceTitleRow>
              <SectionTitle>
                Cuisine Dish Details
              </SectionTitle>
            </WorkSpaceTitleRow>

            {/* <FilledButton>Add Comment
            </FilledButton> */}

            <ButtonContainer>
              <TransactionButton
                name={record?.image ? "Change Image" : "Add Image"}
                onClickHandler={() => onAddImage(record)}
              />

              <TransactionButton
                name="Update Dish"
                onClickHandler={() => onUpdate(record)}
              />

              <TransactionButton
                name="Delete Dish"
                color="error"
                onClickHandler={() => onDelete(record)}
              />
            </ButtonContainer>

          </BlueBorderContainer>

          <ThreeColNRowGrid>
            <div>
              <ShowLabel>Dish ID</ShowLabel>
              <Paragraph>#{record?.id}</Paragraph>
            </div>

            <div>
              <ShowLabel>Dish Name</ShowLabel>
              <Paragraph>{record?.name}</Paragraph>
            </div>

            <div>
              <ShowLabel>Meal Type</ShowLabel>
              <Paragraph>{record?.mealType}</Paragraph>
            </div>

            <div>
              <ShowLabel>Type</ShowLabel>
              <Paragraph>{record?.type}</Paragraph>
            </div>

            <div>
              <ShowLabel>Dish Type</ShowLabel>
              <Paragraph>{record?.dishType}</Paragraph>
            </div>

            <div>
              <ShowLabel>Name In Hindi</ShowLabel>
              <Paragraph>{record?.nameHi}</Paragraph>
            </div>

            <div>
              <ShowLabel>Name In Bengali</ShowLabel>
              <Paragraph>{record?.nameBn}</Paragraph>
            </div>

            <div>
              <ShowLabel>Available For Chefit</ShowLabel>
              <Paragraph>{record?.forChefit ? "Yes" : "No"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Available For Party</ShowLabel>
              <Paragraph>{record?.forParty ? "Yes" : "No"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Category</ShowLabel>
              <Paragraph>{record?.category?.name}</Paragraph>
            </div>

            <div>
              <ShowLabel>Available For Book by Dish</ShowLabel>
              <Paragraph>{record?.forBookByDish ? "Yes" : "No"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Popular</ShowLabel>
              <Paragraph>{record?.isPopular ? "Yes" : "No"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Dish Tier</ShowLabel>
              <Paragraph>{record?.dishTier || "-"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Base Price</ShowLabel>
              <Paragraph>{record?.basePrice != null ? `₹${record.basePrice}` : "-"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Base Time (min)</ShowLabel>
              <Paragraph>{record?.baseTime ?? "-"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Base People</ShowLabel>
              <Paragraph>{record?.basePeople ?? "-"}</Paragraph>
            </div>

            {record?.dishType === "BREAD" && (
              <>
                <div>
                  <ShowLabel>Plate Size (pcs)</ShowLabel>
                  <Paragraph>{record?.plateSize ?? "-"}</Paragraph>
                </div>

                <div>
                  <ShowLabel>Plate Unit</ShowLabel>
                  <Paragraph>{record?.plateUnit || "-"}</Paragraph>
                </div>

                <div>
                  <ShowLabel>Price per Plate</ShowLabel>
                  <Paragraph>{record?.pricePerPlate != null ? `₹${record.pricePerPlate}` : "-"}</Paragraph>
                </div>

                <div>
                  <ShowLabel>Time per Plate (min)</ShowLabel>
                  <Paragraph>{record?.timePerPlate ?? "-"}</Paragraph>
                </div>
              </>
            )}

            <div>
              <ShowLabel>Filter Category</ShowLabel>
              <Paragraph>{record?.filterCategory || "-"}</Paragraph>
            </div>

            <div>
              <ShowLabel>Custom Category Tags</ShowLabel>
              <Paragraph>
                {Array.isArray(record?.customCategoryTags) && record.customCategoryTags.length
                  ? record.customCategoryTags.join(", ")
                  : "-"}
              </Paragraph>
            </div>
          </ThreeColNRowGrid>

          <BlueTOpBorderContainer style={{ marginTop: '20px', paddingTop: '10px' }}>
            <div>
              <ShowLabel>Description</ShowLabel>
              <Paragraph>{record?.description || "-"}</Paragraph>
            </div>
          </BlueTOpBorderContainer>


          <BlueTOpBorderContainer style={{ marginTop: '20px', paddingTop: '10px', alignItems: "flex-start" }}>
            <div style={{ width: "100%" }}>
              <ShowLabel>Dish Instructions</ShowLabel>
              {instructions.length ? (
                instructions.map((instruction: any) => (
                  <div
                    key={instruction?.id || `${instruction?.categoryId}-${instruction?.instruction}`}
                    style={{ marginBottom: "16px" }}
                  >
                    <ShowLabel style={{ marginBottom: "4px" }}>
                      {getInstructionCategoryName(instruction)}
                    </ShowLabel>
                    <Paragraph>{instruction?.instruction || "-"}</Paragraph>
                  </div>
                ))
              ) : (
                <Paragraph>-</Paragraph>
              )}
            </div>
          </BlueTOpBorderContainer>
        </WorkspacePanel>

        <div>
          <WorkspacePanel style={{ maxWidth: "376px" }}>
              <SectionTitle style={{ marginBottom: 12 }}>
                Dish Image
              </SectionTitle>
          {/* Large preview */}
          <div
            title={!record.image ? "Photo Not Available" : "Click to preview"}
            onClick={() => setPreviewOpen(true)}
            style={{
              width: "328px",
              height: "218px",
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              borderRadius: "8px",
              cursor: "pointer",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />

          {/* Optional thumbnail */}
          <div
            onClick={() => setPreviewOpen(true)}
            style={{
              marginTop: "12px",
              width: "100px",
              height: "100px",
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "6px",
              cursor: "pointer",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          />
        </WorkspacePanel>

        <WorkspacePanel style={{marginTop: '16px'}}>
          <BlueBorderContainer style={{paddingBottom: '12px'}}>
              <SectionTitle>
                Dish Equipments
              </SectionTitle>
          </BlueBorderContainer>
            <div style={{ width: "100%" }}>
              {equipments.length ? (
                equipments.map((equipment: any) => (
                  <Paragraph key={equipment?.id || equipment?.equipmentId}>
                    {getEquipmentName(equipment)}
                  </Paragraph>
                ))
              ) : (
                <Paragraph>-</Paragraph>
              )}
            </div>
        </WorkspacePanel>
        </div>
      </div>

      {/* IMAGE PREVIEW DIALOG */}
      {previewOpen && (
        <ImagePreviewerDialog
          src={imageSrc}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* <Show {...props} actions={false}>
        <SimpleShowLayout>
          <NumberField label="Id" source="id" />
          <TextField label="Name" source="name" />
          <TextField label="Meal Type" source="mealType" />
          <TextField label="Type" source="type" />
          <TextField label="Dish Type" source="dishType" />
          <TextField label="Name in Hindi" source="nameHi" />
          <TextField label="Name in Bengali" source="nameBn" />
          <TextField label="Available for Chefit" source="forChefit" />
          <TextField label="Available for Party" source="forParty" />
          <FunctionField
            label="Category"
            render={(record: RaRecord) => (
              record?.category?.name
            )}
          />
          <div style={{ display: "flex", flexDirection: "row", gap: '30px' }}>
            <FunctionField
              label="Dish Image Rectangle"
              render={(record: RaRecord) => (
                <div
                  title={!record?.image ? "Photo Not Available" : ""}
                  onClick={() => setOpen(true)}
                  style={{
                    width: "328px",
                    height: "218px",
                    backgroundImage: `url(${record?.image || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRODZob7ROpqlx8WXwjueNwTOlC6Qum-yMiyfXiFRXkdhkHgybCpdvM1UpaXCL1ycfO8NI&usqp=CAU"})`,
                    backgroundSize: "cover",    // cover / contain / auto
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                />
              )}
            />
            <FunctionField
              label="Dish Image Rectangle"
              render={(record: RaRecord) => (
                <div
                  title={!record?.image ? "Photo Not Available" : ""}
                  onClick={() => setOpen(true)}
                  style={{
                    width: "100px",
                    height: "100px",
                    backgroundImage: `url(${record?.image || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRODZob7ROpqlx8WXwjueNwTOlC6Qum-yMiyfXiFRXkdhkHgybCpdvM1UpaXCL1ycfO8NI&usqp=CAU"})`,
                    backgroundSize: "cover",    // cover / contain / auto
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                />
              )}
            />
          </div>
          <TextField label="Discription" source="description" />
        </SimpleShowLayout>
        {open && <ImagePreviewerDialog src={record?.image} open={open} onClose={() => setOpen(false)} />}
      </Show> */}
    </>
  );
};

export default CuisineMetaShow;
